import React, { useState } from "react";
import {
  Box,
  Paper,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Stack,
  Typography,
} from "@mui/material";
import { Close, Add, DeleteOutline, PhoneOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateAccomodationDetails } from "../../Redux/slices/addEnquirySlice";
import { align_center, align_right, VM_Colors } from "../../Styles/VMStyles";
import MUITypography from "../../Components/MUITypography";
import StyledInputField from "../../Components/Custom/StyledInputField";
import MuiTypographyMandatory from "../../Components/Custom/MuiTypographyMandatory";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_COUNTRY_DATA_KEY } from "../../Util/VMConstants";
import { formatAmount, toTitleCase } from "../../Util/UtilHelper";

const AEEventRoomDetails = ({ tabIndex }) => {
  const dispatch = useDispatch();


  const sessionFactory = new StorageFactory('session');
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';



  const chargeType = useSelector(
    (state) =>
      state.addEnquiry.events[tabIndex]?.accomodationDetails?.charge_type || ""
  );
  const accomodationDetails = useSelector(
    (state) =>
      state.addEnquiry.events[tabIndex]?.accomodationDetails || {}
  );

  const [openPersonDrawer, setOpenPersonDrawer] = useState(false);
  const [personList, setPersonList] = useState([{ name: "", phone: "", cost: "" }]);
  const [numPersons, setNumPersons] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Handle accommodation field change
  const handleChange = (field, value) => {
    if (field === "charge_type") {
      dispatch(updateAccomodationDetails({ index: tabIndex, field: "charge_type", value }));
      dispatch(updateAccomodationDetails({ index: tabIndex, field: "no_of_rooms", value: "" }));
      dispatch(updateAccomodationDetails({ index: tabIndex, field: "persons_data", value: {} }));
    } else {
      dispatch(updateAccomodationDetails({ index: tabIndex, field, value }));
    }
  };

  // Open Drawer (load existing data if available)
  const handlePersonDrawerOpen = () => {
    if (accomodationDetails.persons_data?.data?.length) {
      setPersonList(accomodationDetails.persons_data.data);
      setNumPersons(accomodationDetails.persons_data.data.length);
    } else {
      setPersonList([{ name: "", phone: "", cost: "" }]);
      setNumPersons(1);
    }
    setOpenPersonDrawer(true);
  };

  // Add new person card
  const handleAddPersonCard = () => {
    setPersonList([
      ...personList,
      { name: "", phone: "", cost: personList[0]?.cost || "" },
    ]);
    setNumPersons((prev) => prev + 1);
  };

  // Update person fields
  const handlePersonChange = (index, key, value) => {
    setPersonList((prev) =>
      prev.map((p, i) =>
        i === index
          ? { ...p, [key]: value }
          : key === "cost"
          ? { ...p, cost: value } // update cost for all persons
          : p
      )
    );

    // Clear validation error when user fixes input
    const errorKey = key === "cost" ? "cost" : `${key}_${index}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Delete person from list
  const handleDeletePerson = (index) => {
    setPersonList((prev) => prev.filter((_, i) => i !== index));
    setNumPersons((prev) => Math.max(prev - 1, 1));
  };

  // Validation function
  const validatePersons = () => {
    let valid = true;
    let newErrors = {};

    // validate cost only once
    if (!personList[0]?.cost || Number(personList[0]?.cost) <= 0) {
      valid = false;
      newErrors["cost"] = "Cost must be greater than 0";
    }

    personList.forEach((p, index) => {
      if (!p.name || p.name.trim().length < 2) {
        valid = false;
        newErrors[`name_${index}`] = "Name must be at least 2 characters";
      }
      if (!p.phone || !/^\d{10}$/.test(p.phone)) {
        valid = false;
        newErrors[`phone_${index}`] = "Phone must be 10 digits";
      }
    });

    setErrors(newErrors);
    return valid;
  };

  // Submit and save to Redux
  const handlePersonSubmit = () => {
    setSubmitted(true);
    if (!validatePersons()) return;

    const total_cost = personList.reduce(
      (sum, p) => sum + Number(p.cost || 0),
      0
    );
    dispatch(
      updateAccomodationDetails({
        index: tabIndex,
        field: "persons_data",
        value: { total_cost, data: personList },
      })
    );
    setOpenPersonDrawer(false);
    setSubmitted(false);
  };

  return (
    <Paper sx={{ mt: 2, p: 2, backgroundColor: VM_Colors.white1 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MUITypography variant='h3' >Accommodation (Optional)</MUITypography>
        <Box sx={align_right}>
          {chargeType === "person" && (
            <Button
              variant="contained"
              color="primary"
              onClick={handlePersonDrawerOpen}
            >
              Add Person
            </Button>
          )}
        </Box>
      </Box>

      {/* Radio selection */}
      <RadioGroup
        row
        name="chargeType"
        value={chargeType}
        onChange={(e) => handleChange("charge_type", e.target.value)}
      >
        <FormControlLabel
          value="room"
          control={
            <Radio
              sx={{
                color: "grey.600",
                "&.Mui-checked": { color: VM_Colors.success },
              }}
            />
          }
          label="Charge per room"
        />
        <FormControlLabel
          value="person"
          control={
            <Radio
              sx={{
                color: "grey.600",
                "&.Mui-checked": { color: VM_Colors.success },
              }}
            />
          }
          label="Charge per person"
        />
      </RadioGroup>

      {/* Rooms */}
      {chargeType === "room" && (
        <Box mt={2}>
          <StyledInputField
            heading="Number of Rooms"
            type="number"
            fullWidth
            onWheel={(e) => e.target.blur()}
            value={accomodationDetails.no_of_rooms || ""}
            onChange={(e) =>
              dispatch(
                updateAccomodationDetails({
                  index: tabIndex,
                  field: "no_of_rooms",
                  value: e.target.value,
                })
              )
            }
          />
        </Box>
      )}

      {/* Persons List (display after submit) */}
      {chargeType === "person" && accomodationDetails.persons_data?.data && (
        <Box
          sx={{
            display: "grid",
            gridAutoFlow: "column",
            gridTemplateRows:
              accomodationDetails.persons_data.data.length > 2 ? "1fr 1fr" : "auto",
            gridAutoColumns: "260px",
            gap: 1.5,
            mt: 2,
            pb: 1,
            overflowX: "auto",
            overflowY: "hidden",
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#CBD5E1",
              borderRadius: 999,
            },
          }}
        >
          {accomodationDetails.persons_data.data.map((person, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                minWidth: 0,
                scrollSnapAlign: "start",
                p: 1.75,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                borderRadius: 2.5,
                border: `1px solid ${VM_Colors.light_gray}`,
                bgcolor: VM_Colors.white,
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(30, 58, 138, 0.1)",
                  color: VM_Colors.primary,
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                {toTitleCase(person.name || "?").charAt(0)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#64748B",
                    mb: 0.15,
                  }}
                >
                  Guest {index + 1}
                </Typography>
                <MUITypography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}
                >
                  {toTitleCase(person.name || "") || "—"}
                </MUITypography>
                <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
                  <PhoneOutlined sx={{ fontSize: 15, color: "#64748B" }} />
                  <MUITypography sx={{ color: "#334155", fontSize: 14, fontWeight: 500 }}>
                    {person.phone || "N/A"}
                  </MUITypography>
                </Box>
                <MUITypography
                  sx={{ color: VM_Colors.primary, fontWeight: 700, fontSize: 14, mt: 0.5 }}
                >
                  {formatAmount(person.cost || 0, currencySymbol)}
                </MUITypography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  const updatedList = accomodationDetails.persons_data.data.filter(
                    (_, i) => i !== index
                  );
                  const total_cost = updatedList.reduce(
                    (sum, p) => sum + Number(p.cost || 0),
                    0
                  );
                  dispatch(
                    updateAccomodationDetails({
                      index: tabIndex,
                      field: "persons_data",
                      value: { total_cost, data: updatedList },
                    })
                  );
                }}
                sx={{
                  color: "#94A3B8",
                  "&:hover": { color: VM_Colors.error || "#D32F2F", bgcolor: "rgba(211,47,47,0.08)" },
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Box>
      )}

      {/* Drawer for Adding Persons */}
      <Drawer
        anchor="right"
        open={openPersonDrawer}
        onClose={() => setOpenPersonDrawer(false)}
      >
        <Box sx={{ width: 400, p: 3, position: "relative" }}>
          <IconButton
            onClick={() => setOpenPersonDrawer(false)}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <Close />
          </IconButton>

          <MUITypography variant="h2" sx={{ m: 3, ...align_center }}>
            <b>Add Person</b>
          </MUITypography>

          {/* No of Persons */}
          <MuiTypographyMandatory label="No. of Persons" required></MuiTypographyMandatory>
          <FormControl fullWidth sx={{ mb: 2 }}>
            {/* <InputLabel>No. of Persons</InputLabel> */}
            <Select
              value={numPersons}
              displayEmpty
              onChange={(e) => {
                const val = Number(e.target.value);
                setNumPersons(val);
                const cost = personList[0]?.cost || "";
                const newList = Array.from({ length: val }, (_, i) => ({
                  name: personList[i]?.name || "",
                  phone: personList[i]?.phone || "",
                  cost,
                }));
                setPersonList(newList);
              }}
            >
              <MenuItem value="" disabled>-- Select --</MenuItem>
              {Array.from({ length: 15 }, (_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Cost */}
          <StyledInputField
            heading="Cost per Person"
            type="number"
            fullWidth
            required
            value={personList[0]?.cost || ""}
            onChange={(e) => handlePersonChange(0, "cost", e.target.value)}
            onWheel={(e) => e.target.blur()}
            error={submitted && !!errors["cost"]}
            helperText={submitted && errors["cost"] ? errors["cost"] : ""}
          />

          {/* Person Details */}
          <Stack spacing={2} mt={3}>
            {personList.map((person, index) => (
              <Paper key={index} sx={{ p: 2, position: "relative" }}>
                <IconButton
                  size="small"
                  sx={{ position: "absolute", top: 8, right: 8 }}
                  onClick={() => handleDeletePerson(index)}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>

                <MUITypography variant="subtitle2" mb={1}>
                  Person {index + 1}
                </MUITypography>
                <StyledInputField
                  heading="Person Name"
                  value={person.name}
                  required
                  onChange={(e) =>
                    handlePersonChange(index, "name", e.target.value)
                  }
                  error={submitted && !!errors[`name_${index}`]}
                  helperText={
                    submitted && errors[`name_${index}`]
                      ? errors[`name_${index}`]
                      : ""
                  }
                />
                
                <StyledInputField
                    heading="Phone Number"
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    value={person.phone}
                    required 
                    inputProps={{ 
                      maxLength: 15, 
                      inputMode: "numeric", // for mobile number keypad
                      pattern: "[0-9]*",    // ensures only numbers
                    }}
                    onChange={(e) =>
                      handlePersonChange(index, "phone", e.target.value.slice(0, 10))
                    }
                    error={submitted && !!errors[`phone_${index}`]}
                    helperText={submitted && errors[`phone_${index}`] ? errors[`phone_${index}`] : ""}
                  />
              </Paper>
            ))}

            <Button startIcon={<Add />} onClick={handleAddPersonCard}>
              Add Another Person
            </Button>
            <Button variant="contained" onClick={handlePersonSubmit}>
              Submit
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Paper>
  );
};

export default AEEventRoomDetails;
